<script setup lang="ts">
import { useOverlayScrollbars } from 'overlayscrollbars-vue'
import { onMounted } from 'vue'

import { initDarkmode } from '/@/store/modules/darkmode'
import { isEmpty } from '/@/utils/is'

// This is the global app setup function

/**
 * Initialize the darkmode watcher
 *
 * @see /@/store/darkmode
 */
initDarkmode()

// When downloading anything in app it will trigger download manager
const downloadStore = useDownloadsStore()
const { downloads } = storeToRefs(downloadStore)

// This is used to show the update screen when the app is being updated
const updateScreen = useUpdateScreenStore()
const { isUpdateScreenVisible } = storeToRefs(updateScreen)

const route = useRoute()

// UI Kit: Auth-dependent code removed
// - isLoggedIn watch
// - useFilterAIModal()
// - fetchConfig, fetchMyLicense
// - getCsrfToken

// Creating a ref called 'bodyOverlayScrollbarsApplied'.
// This ref is used to track whether the overlay scrollbars have been applied or not.
// The initial value is set to 'null'.

const bodyOverlayScrollbarsApplied = ref<boolean | null>(null)

// Calling the 'useOverlayScrollbars' hook to add overlay scrollbars to the body element.
// This hook returns a function called 'initBodyOverlayScrollbars' to initialize the scrollbars.
// We also define callback functions for the initialize and destroy events of the scrollbars.

const [initBodyOverlayScrollbars] = useOverlayScrollbars({
  defer: true,
  events: {
    initialized: () => {
      bodyOverlayScrollbarsApplied.value = true
    },
    destroyed: () => {
      bodyOverlayScrollbarsApplied.value = false
    },
  },
})

// When component is mounted to the DOM, calling 'initBodyOverlayScrollbars' to apply overlay scrollbars to the body element.
onMounted(() => initBodyOverlayScrollbars(document.body))
</script>

<template>
  <div class="min-h-inherit">
    <Suspense>
      <RouterView v-slot="{ Component }">
        <Component :is="Component" />
      </RouterView>
    </Suspense>

    <!-- Add router modal -->
    <!-- $route.fullPath as key purpose forcing replacement -->
    <RouterModal :key="route.name" />
    <RouterDrawer />
    <ReloadPrompt app-name="Brandefense" />
    <DownloadManager v-if="!isEmpty(downloads)" :model-value="downloads" />
    <Notification />

    <UpdateScreen v-if="isUpdateScreenVisible" />
  </div>
</template>
